import React, { useEffect } from 'react'

import { useBrand } from '../../contexts/BrandContext'

const DEFAULT_TITLE = ' – Free AI Expert Agents On Demand'
const DEFAULT_IMAGE = ''
const DEFAULT_DESCRIPTION = ''
const DEFAULT_URL = import.meta.env.VITE_SITE_ORIGIN as string

interface OpenGraphProps {
  title: string
  description?: string
  image?: string
  url: string
  type?: 'website' | 'article' | 'profile'
  locale?: string
}

export const Meta: React.FC<OpenGraphProps> = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  locale = 'en_US',
}) => {
  const brand = useBrand()
  const metaTitle = `${title} | ${brand.brandName}`

  useEffect(() => {
    // ◼️ Apply new tags
    document.title = metaTitle
    setMeta('title', metaTitle)
    setMeta('description', description || '')

    setMeta('og:type', type)
    setMeta('og:url', url)
    setMeta('og:title', metaTitle)
    setMeta('og:description', description || '')
    setMeta('og:image', image)
    setMeta('og:site_name', brand.brandName)
    setMeta('og:locale', locale)

    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:url', url)
    setMeta('twitter:title', metaTitle)
    setMeta('twitter:description', description || '')
    setMeta('twitter:image', image)

    // ◼️ Cleanup → set default
    return () => {
      document.title = brand.brandName + DEFAULT_TITLE
      setMeta('title', brand.brandName + DEFAULT_TITLE)
      setMeta('description', DEFAULT_DESCRIPTION)

      setMeta('og:type', 'website')
      setMeta('og:url', DEFAULT_URL)
      setMeta('og:title', brand.brandName + DEFAULT_TITLE)
      setMeta('og:description', DEFAULT_DESCRIPTION)
      setMeta('og:image', DEFAULT_IMAGE)
      setMeta('og:site_name', brand.brandName)
      setMeta('og:locale', 'en_US')

      setMeta('twitter:card', 'summary_large_image')
      setMeta('twitter:url', DEFAULT_URL)
      setMeta('twitter:title', brand.brandName + DEFAULT_TITLE)
      setMeta('twitter:description', DEFAULT_DESCRIPTION)
      setMeta('twitter:image', DEFAULT_IMAGE)
    }
  }, [metaTitle, description, image, url, type, locale])

  return null
}

function setMeta(name: string, value: string) {
  let el =
    document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`) ??
    document.querySelector<HTMLMetaElement>(`meta[property="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      el.setAttribute('property', name)
    } else el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', value)
}
